import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studioOnboarding } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

const QUESTIONS = [
  { id: 'company_size', question: 'How many clients does your MSP serve?', options: ['1-10', '11-50', '51-200', '200+'] },
  { id: 'verticals', question: 'Which industries do most of your clients come from?', options: ['Healthcare', 'Legal', 'Finance', 'Manufacturing', 'Retail', 'Government', 'Mixed'] },
  { id: 'biggest_challenge', question: "What's your biggest pain point with client relationships right now?", options: ["Clients don't know what we do for them", 'QBRs take too long to prepare', 'Ticket visibility complaints', 'Security questionnaires', 'Onboarding new clients'] },
  { id: 'first_app', question: 'If you could wave a wand, what would your first client app do?', options: ['Show clients their ticket status', 'Give them a security score', 'Replace email for support', 'Automate QBR reports', 'Onboard new clients faster'] },
  { id: 'psa_current', question: 'Which PSA are you currently using?', options: ['The One PSA', 'ConnectWise', 'Autotask', 'Halo PSA', 'Syncro', 'None yet'] },
];

export function StudioOnboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [completing, setCompleting] = useState(false);

  const currentQ = QUESTIONS[step]!;
  const isLast = step === QUESTIONS.length - 1;

  function selectAnswer(answer: string) {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: answer }));
  }

  async function handleNext() {
    if (!answers[currentQ.id]) return;

    if (isLast) {
      setCompleting(true);
      try {
        await studioOnboarding.complete(answers);
        await refresh();
        navigate('/studio/ai-division');
      } finally {
        setCompleting(false);
      }
    } else {
      setStep((s) => s + 1);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="mb-8">
          <div className="flex gap-1.5">
            {QUESTIONS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2 text-right">
            {step + 1} of {QUESTIONS.length}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{currentQ.question}</h2>

          <div className="space-y-2 mb-8">
            {currentQ.options.map((option) => (
              <button
                key={option}
                onClick={() => selectAnswer(option)}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                  answers[currentQ.id] === option
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
              >
                ← Back
              </button>
            )}
            <button
              onClick={() => void handleNext()}
              disabled={!answers[currentQ.id] || completing}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl text-sm transition-colors"
            >
              {completing ? 'Building your profile...' : isLast ? 'Get My Recommendations →' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
